import { Map } from "@iventis/mapbox-gl";
import { Listener } from "../../types/internal";
import { bustTileCache } from "./tile-requests";

/**
 * A class to manage Mapbox vector sources and their tile refreshing.
 */
export class MapboxVectorSourceManager {
    private mapIdleListener: Listener | undefined;

    private sourceLoadedListener: Listener | undefined;

    private resolveQueue: (() => void)[] = [];

    constructor(private readonly map: Map) {}

    private onMapIdle(callback: () => void): Listener {
        let removed = false;
        const mapIdleFunction = () => {
            if (removed) {
                return;
            }
            callback();
        };

        this.map.once("idle", mapIdleFunction);
        return {
            remove: () => {
                removed = true;
                this.map.off("idle", mapIdleFunction);
            },
        };
    }

    // Currently only supports same tile source names
    public async refreshTileSources(tileSourceNames: string[]) {
        // If we are already waiting to refresh tiles, remove the previous listeners
        this.mapIdleListener?.remove();
        this.sourceLoadedListener?.remove();

        // Queue the resolve function
        const refreshPromise = new Promise<void>((resolve) => {
            this.resolveQueue.push(resolve);
        });

        let settingSourceTiles = false;
        const doRefresh = () => {
            tileSourceNames.forEach((sourceName) => {
                const source = this.map.getSource(sourceName);
                if (source != null && source.type === "vector" && source.tiles != null) {
                    const newTileUrls = bustTileCache(source.tiles);
                    source.setTiles(newTileUrls);
                    settingSourceTiles = true;
                }
            });

            if (settingSourceTiles) {
                this.sourceLoadedListener = this.onMapIdle(() => {
                    this.resolveQueue.forEach((resolve) => resolve());
                    this.resolveQueue = [];
                });
            } else {
                this.resolveQueue.forEach((resolve) => resolve());
                this.resolveQueue = [];
            }
        };

        if (this.map.idle()) {
            doRefresh();
        } else {
            this.mapIdleListener = this.onMapIdle(doRefresh);
        }

        return refreshPromise;
    }
}
